import { useCallback, useEffect, useMemo, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Database, HardDrive, Trash2, ImageOff, RefreshCw, AlertTriangle, BarChart3, Calendar } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer, Legend } from "recharts";
import { toast } from "sonner";
import { api } from "@/lib/api";

function fmtBytes(b) {
  if (!b || b === 0) return "0 B";
  const u = ["B", "KB", "MB", "GB", "TB"];
  let i = 0; let n = Number(b);
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(n >= 100 ? 0 : 1)} ${u[i]}`;
}
function toDdMmYyyy(iso) { if (!iso) return "—"; const [y,m,d] = String(iso).split("-"); return d&&m&&y ? `${d}.${m}.${y}` : iso; }
function toMmYyyy(ym)   { if (!ym) return "—"; const [y,m] = String(ym).split("-"); return m&&y ? `${m}.${y}` : ym; }

export default function StorageManagementPage() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [active, setActive] = useState("attendance");
  const [bucket, setBucket] = useState("month"); // month | day
  const [drilldown, setDrilldown] = useState(null); // { source, key, kind:'day'|'month' }
  const [confirmDel, setConfirmDel] = useState(null); // { title, body, doFn, requireType }
  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/storage/overview");
      setData(data);
    } catch (e) {
      console.error(e); toast.error("Could not load storage overview");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const sources = useMemo(() => data?.sources || [], [data]);
  const other = data?.other;
  const totals = data?.totals || {};
  const dbStats = data?.db_stats || {};
  const activeSrc = useMemo(() => sources.find((s) => s.source === active), [sources, active]);

  // Stack chart data — last 12 months across all tracked sources
  const stackData = useMemo(() => {
    const map = {};
    for (const s of sources) {
      for (const m of s.by_month || []) {
        if (!map[m.ym]) map[m.ym] = { ym: m.ym, label: toMmYyyy(m.ym) };
        map[m.ym][`${s.source}_photo`] = (map[m.ym][`${s.source}_photo`] || 0) + (m.photo_bytes || 0);
        map[m.ym][`${s.source}_data`] = (map[m.ym][`${s.source}_data`] || 0) + (m.data_bytes || 0);
      }
    }
    return Object.values(map).sort((a,b) => a.ym.localeCompare(b.ym)).slice(-12);
  }, [sources]);

  return (
    <AppShell title="Storage Management" subtitle="Database & photo footprint · prune by row, day, or month">
      <div className="space-y-5" data-testid="storage-management-page">
        {/* KPI strip */}
        <Card>
          <CardContent className="flex flex-wrap items-center gap-4 p-5">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-100 text-amber-700"><HardDrive className="h-5 w-5" /></div>
            <div className="grid flex-1 grid-cols-2 gap-3 sm:grid-cols-4 md:grid-cols-5">
              <Kpi label="DB storage" value={fmtBytes(dbStats.storage_size)} sub="PostgreSQL on disk" testid="storage-kpi-db" />
              <Kpi label="Tracked total" value={fmtBytes(totals.total_bytes)} sub={`${totals.doc_count || 0} docs`} testid="storage-kpi-total" />
              <Kpi label="Photos" value={fmtBytes(totals.photo_bytes)} sub="across tracked sources" tone="rose" testid="storage-kpi-photos" />
              <Kpi label="Metadata" value={fmtBytes(totals.data_bytes)} sub="text/structured rows" testid="storage-kpi-data" />
              <Kpi label="Other" value={fmtBytes((other?.totals||{}).total_bytes)} sub={`${(other?.totals||{}).doc_count||0} docs`} testid="storage-kpi-other" />
            </div>
            <Button variant="outline" onClick={reload} disabled={loading} data-testid="storage-reload"><RefreshCw className={`mr-1 h-4 w-4 ${loading?'animate-spin':''}`} />Reload</Button>
          </CardContent>
        </Card>

        {loading ? <Skeleton className="h-[420px] w-full rounded-xl" /> : (
          <>
            {/* Stacked bar chart */}
            <Card>
              <CardContent className="p-4 sm:p-5">
                <div className="mb-2 flex items-center gap-2">
                  <BarChart3 className="h-4 w-4 text-amber-700" />
                  <div className="font-display text-sm font-semibold">Last 12 months · stacked by source (photos vs data)</div>
                </div>
                <div className="h-[260px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={stackData} margin={{ top: 6, right: 8, left: 8, bottom: 0 }}>
                      <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
                      <YAxis tickFormatter={fmtBytes} tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} width={56} />
                      <RTooltip formatter={(v) => fmtBytes(v)} contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Bar dataKey="attendance_photo"        name="Attendance · photos"        stackId="a" fill="#fb7185" />
                      <Bar dataKey="attendance_data"         name="Attendance · data"          stackId="a" fill="#fda4af" />
                      <Bar dataKey="cleaning_photo"          name="Cleaning · photos"          stackId="a" fill="#f97316" />
                      <Bar dataKey="cleaning_data"           name="Cleaning · data"            stackId="a" fill="#fdba74" />
                      <Bar dataKey="monthly_feedback_photo"  name="Feedback · photos"          stackId="a" fill="#a855f7" />
                      <Bar dataKey="monthly_feedback_data"   name="Feedback · data"            stackId="a" fill="#d8b4fe" />
                      <Bar dataKey="salary_verifications_photo" name="Salary · photos"         stackId="a" fill="#0ea5e9" />
                      <Bar dataKey="salary_verifications_data"  name="Salary · data"           stackId="a" fill="#bae6fd" radius={[4,4,0,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Source tabs */}
            <Tabs value={active} onValueChange={setActive}>
              <TabsList className="flex w-full flex-wrap justify-start gap-1 bg-slate-100/70 p-1">
                {sources.map((s) => (
                  <TabsTrigger key={s.source} value={s.source} className="data-[state=active]:bg-white" data-testid={`storage-tab-${s.source}`}>
                    {s.label} <Badge variant="outline" className="ml-1.5 font-mono">{fmtBytes(s.totals.total_bytes)}</Badge>
                  </TabsTrigger>
                ))}
                <TabsTrigger value="other" className="data-[state=active]:bg-white" data-testid="storage-tab-other">Other <Badge variant="outline" className="ml-1.5 font-mono">{fmtBytes((other?.totals||{}).total_bytes)}</Badge></TabsTrigger>
              </TabsList>
            </Tabs>

            {active === "other" ? (
              <OtherList other={other} onConfirmDelete={(r) => setConfirmDel({
                title: `Wipe collection "${r.collection}"?`,
                body: `${r.doc_count} document(s) · ${fmtBytes(r.total_bytes)}. This empties the entire collection and cannot be undone. Type DELETE to enable the button.`,
                requireType: true,
                doFn: async () => {
                  const { data } = await api.delete("/admin/storage/other-collection", { params: { name: r.collection, confirm: "DELETE" } });
                  toast.success(`Wiped ${data.deleted} document(s) from ${data.collection}`); await reload();
                },
              })} />
            ) : activeSrc ? (
              <SourceDetail src={activeSrc}
                bucket={bucket} setBucket={setBucket}
                onDrillDay={(date) => setDrilldown({ source: activeSrc.source, label: activeSrc.label, key: date, kind: "day" })}
                onDrillMonth={(ym) => setDrilldown({ source: activeSrc.source, label: activeSrc.label, key: ym, kind: "month" })}
                onConfirmDeleteDay={(row) => setConfirmDel({
                  title: `Delete every ${activeSrc.label} record on ${toDdMmYyyy(row.date)}?`,
                  body: `${row.doc_count} document(s) · ${fmtBytes(row.total_bytes)}. This cannot be undone.`,
                  requireType: false,
                  doFn: async () => {
                    const { data } = await api.delete("/admin/storage/day", { params: { source: activeSrc.source, date: row.date } });
                    toast.success(`Deleted ${data.deleted} record(s)`); await reload();
                  },
                })}
                onConfirmDeleteMonth={(row) => setConfirmDel({
                  title: `Delete every ${activeSrc.label} record for ${toMmYyyy(row.ym)}?`,
                  body: `${row.doc_count} document(s) · ${fmtBytes(row.total_bytes)}. This is a month-level wipe and cannot be undone.`,
                  requireType: true,
                  doFn: async () => {
                    const { data } = await api.delete("/admin/storage/month", { params: { source: activeSrc.source, ym: row.ym, confirm: "DELETE" } });
                    toast.success(`Deleted ${data.deleted} record(s)`); await reload();
                  },
                })}
                onConfirmStripDay={(row) => setConfirmDel({
                  title: `Strip photos for ${activeSrc.label} on ${toDdMmYyyy(row.date)}?`,
                  body: `Audit rows are kept; only photo bytes (~${fmtBytes(row.photo_bytes)}) are wiped.`,
                  requireType: false, label: "Strip photos",
                  doFn: async () => {
                    const { data } = await api.post("/admin/storage/strip-photos", { source: activeSrc.source, date: row.date });
                    toast.success(`Stripped ${data.modified} doc(s) · ~${fmtBytes(data.bytes_saved)} freed`); await reload();
                  },
                })}
                onConfirmStripMonth={(row) => setConfirmDel({
                  title: `Strip photos for ${activeSrc.label} in ${toMmYyyy(row.ym)}?`,
                  body: `Audit rows are kept; only photo bytes (~${fmtBytes(row.photo_bytes)}) are wiped.`,
                  requireType: false, label: "Strip photos",
                  doFn: async () => {
                    const { data } = await api.post("/admin/storage/strip-photos", { source: activeSrc.source, ym: row.ym });
                    toast.success(`Stripped ${data.modified} doc(s) · ~${fmtBytes(data.bytes_saved)} freed`); await reload();
                  },
                })}
              />
            ) : null}
          </>
        )}
      </div>

      <RowDrillDownDialog
        open={!!drilldown}
        onOpenChange={(v)=>!v && setDrilldown(null)}
        drilldown={drilldown}
        onDelete={async (r) => {
          await api.delete("/admin/storage/row", { params: { source: drilldown.source, doc_id: r.id } });
          toast.success("Deleted"); await reload();
        }}
        onStrip={async (r) => {
          const { data } = await api.post("/admin/storage/strip-photos", { source: drilldown.source, doc_id: r.id });
          toast.success(`Stripped photos · ~${fmtBytes(data.bytes_saved)} freed`); await reload();
        }}
      />
      <ConfirmDeleteDialog confirm={confirmDel} onClose={() => setConfirmDel(null)} />
    </AppShell>
  );
}

function Kpi({ label, value, sub, tone="slate", testid }) {
  const map = { slate:"text-slate-800", rose:"text-rose-700", amber:"text-amber-700", emerald:"text-emerald-700" };
  return (
    <div className="flex flex-col rounded-xl border bg-slate-50/60 px-3 py-2" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-base font-semibold tabular-nums md:text-lg ${map[tone]||map.slate}`}>{value}</div>
      {sub ? <div className="text-[10px] text-slate-500">{sub}</div> : null}
    </div>
  );
}

function OtherList({ other, onConfirmDelete }) {
  const rows = other?.by_collection || [];
  const PROTECTED = new Set([
    "users", "settings", "parks", "contractors", "park_assets",
    "payroll_rates", "auth_tokens",
  ]);
  return (
    <Card>
      <CardContent className="p-4 sm:p-5">
        <div className="mb-3 flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-900">
          <AlertTriangle className="h-4 w-4 shrink-0 text-rose-700" />
          <div>
            <b>Danger zone.</b> These collections back the app itself (auth, lookups, audit trails).
            Wiping any of them can break features. The marked <span className="rounded border border-slate-300 bg-white px-1 font-mono text-[10px]">core</span>
            collections are protected and cannot be deleted from this page even with confirmation.
          </div>
        </div>
        <div className="overflow-hidden rounded-lg border" data-testid="storage-other-list">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left">Collection</th>
                <th className="px-3 py-2 text-right">Docs</th>
                <th className="px-3 py-2 text-right">Total</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {rows.length === 0 ? (
                <tr><td colSpan={4} className="px-4 py-6 text-center text-sm text-slate-500">No other collections.</td></tr>
              ) : rows.map((r) => {
                const isProtected = PROTECTED.has(r.collection);
                return (
                  <tr key={r.collection} className="hover:bg-amber-50/30">
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <span className="font-mono">{r.collection}</span>
                        {isProtected ? <Badge variant="outline" className="border-slate-300 bg-slate-50 text-[10px] uppercase tracking-wider text-slate-700">core</Badge> : null}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.doc_count}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-semibold">{fmtBytes(r.total_bytes)}</td>
                    <td className="px-3 py-2 text-right">
                      <Button
                        size="sm"
                        variant="destructive"
                        disabled={isProtected}
                        title={isProtected ? "Protected — cannot be wiped from this UI" : "Wipe every document in this collection"}
                        onClick={() => onConfirmDelete(r)}
                        data-testid={`storage-other-delete-${r.collection}`}
                      ><Trash2 className="mr-1 h-3.5 w-3.5" />{isProtected ? "Locked" : "Wipe collection"}</Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

function SourceDetail({ src, bucket, setBucket, onDrillDay, onDrillMonth, onConfirmDeleteDay, onConfirmDeleteMonth, onConfirmStripDay, onConfirmStripMonth }) {
  // Show BOTH Month-wise (top) and Day-wise (bottom) on the same screen so
  // the day-level + line-level delete actions are never hidden behind a
  // toggle. Each row has Inspect → opens per-row delete modal.
  const months = (src.by_month || []);
  const days = (src.by_day || []).slice(-30).reverse();
  return (
    <div className="space-y-4">
      <BucketTable
        title={`${src.label} · Month-wise`}
        rows={months}
        keyOf={(r) => r.ym}
        labelOf={(r) => toMmYyyy(r.ym)}
        onDrill={(r) => onDrillMonth(r.ym)}
        onConfirmStrip={onConfirmStripMonth}
        onConfirmDelete={onConfirmDeleteMonth}
        emptyText="No data captured yet."
        testidPrefix="storage-month"
      />
      <BucketTable
        title={`${src.label} · Day-wise (last 30 days)`}
        rows={days}
        keyOf={(r) => r.date}
        labelOf={(r) => toDdMmYyyy(r.date)}
        onDrill={(r) => onDrillDay(r.date)}
        onConfirmStrip={onConfirmStripDay}
        onConfirmDelete={onConfirmDeleteDay}
        emptyText="No daily activity in the last 30 days."
        testidPrefix="storage-day"
      />
    </div>
  );
}

function BucketTable({ title, rows, keyOf, labelOf, onDrill, onConfirmStrip, onConfirmDelete, emptyText, testidPrefix }) {
  return (
    <Card>
      <CardContent className="p-4 sm:p-5">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div className="font-display text-sm font-semibold">{title}</div>
          <span className="text-[11px] text-slate-500">Inspect opens per-row delete · Strip wipes only photos</span>
        </div>
        {rows.length === 0 ? (
          <div className="rounded border border-dashed border-slate-200 bg-slate-50/50 px-4 py-6 text-center text-sm text-slate-500">{emptyText}</div>
        ) : (
          <div className="overflow-hidden rounded-lg border">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left">When</th>
                  <th className="px-3 py-2 text-right">Docs</th>
                  <th className="px-3 py-2 text-right">Photos</th>
                  <th className="px-3 py-2 text-right">Data</th>
                  <th className="px-3 py-2 text-right">Total</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {rows.map((row) => {
                  const k = keyOf(row);
                  return (
                    <tr key={k} className="hover:bg-amber-50/40" data-testid={`${testidPrefix}-row-${k}`}>
                      <td className="px-3 py-2 font-mono">{labelOf(row)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{row.doc_count}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-rose-700">{fmtBytes(row.photo_bytes)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtBytes(row.data_bytes)}</td>
                      <td className="px-3 py-2 text-right tabular-nums font-semibold">{fmtBytes(row.total_bytes)}</td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap justify-end gap-1.5">
                          <Button size="sm" variant="ghost" onClick={() => onDrill(row)} data-testid={`${testidPrefix}-drill-${k}`}>View rows</Button>
                          <Button size="sm" variant="outline" onClick={() => onConfirmStrip(row)} disabled={!row.photo_bytes} data-testid={`${testidPrefix}-strip-${k}`}><ImageOff className="mr-1 h-3.5 w-3.5" />Strip photos</Button>
                          <Button size="sm" variant="destructive" onClick={() => onConfirmDelete(row)} data-testid={`${testidPrefix}-delete-${k}`}><Trash2 className="mr-1 h-3.5 w-3.5" />Delete</Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function RowDrillDownDialog({ open, onOpenChange, drilldown, onDelete, onStrip }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!open || !drilldown) return;
    let cancel = false; setLoading(true);
    const params = drilldown.kind === "day"
      ? { source: drilldown.source, date: drilldown.key }
      : { source: drilldown.source, ym: drilldown.key };
    api.get("/admin/storage/rows", { params }).then(({ data }) => { if (!cancel) setRows(data || []); })
      .catch(() => { if (!cancel) setRows([]); }).finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [open, drilldown]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="storage-row-dialog">
        <DialogHeader>
          <DialogTitle>{drilldown ? `${drilldown.label} · ${drilldown.kind === "day" ? toDdMmYyyy(drilldown.key) : toMmYyyy(drilldown.key)}` : ""}</DialogTitle>
          <DialogDescription>Individual rows — delete or strip photos per row. Up to 200 most-recent shown.</DialogDescription>
        </DialogHeader>
        {loading ? (
          <Skeleton className="h-40" />
        ) : (
          <div className="max-h-[55vh] overflow-auto rounded-lg border">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                <tr><th className="px-2 py-1 text-left">Row</th><th className="px-2 py-1 text-right">Photos</th><th className="px-2 py-1 text-right">Data</th><th className="px-2 py-1 text-right">Actions</th></tr>
              </thead>
              <tbody className="divide-y">
                {rows.length === 0 ? <tr><td colSpan={4} className="px-2 py-6 text-center text-slate-500">No rows.</td></tr> :
                rows.map((r) => (
                  <tr key={r.id} className="hover:bg-amber-50/30" data-testid={`storage-row-detail-${r.id}`}>
                    <td className="px-2 py-1.5">
                      <div className="flex flex-col">
                        <span className="font-medium">{r.employee_name || r.captured_by_user_name || r.asset_name || r.id}</span>
                        <span className="text-[10px] text-slate-500">{r.park_name ? `${r.park_name} · ` : ""}{r.kind ? `${r.kind} · ` : ""}{r.employee_code || ""}</span>
                      </div>
                    </td>
                    <td className="px-2 py-1.5 text-right tabular-nums text-rose-700">{fmtBytes(r.photo_bytes)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{fmtBytes(r.data_bytes)}</td>
                    <td className="px-2 py-1.5">
                      <div className="flex flex-wrap justify-end gap-1">
                        <Button size="sm" variant="outline" disabled={!r.photo_bytes} onClick={async () => { await onStrip(r); setRows((p)=>p.map((x)=>x.id===r.id?{...x,photo_bytes:0}:x)); }} data-testid={`storage-row-strip-${r.id}`}><ImageOff className="mr-1 h-3 w-3" />Strip</Button>
                        <Button size="sm" variant="destructive" onClick={async () => { if(!window.confirm('Delete this row?')) return; await onDelete(r); setRows((p)=>p.filter((x)=>x.id!==r.id)); }} data-testid={`storage-row-delete-${r.id}`}><Trash2 className="mr-1 h-3 w-3" />Delete</Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ConfirmDeleteDialog({ confirm, onClose }) {
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setTyped(""); setBusy(false); }, [confirm]);
  if (!confirm) return null;
  const blocked = !!confirm.requireType && typed.trim().toUpperCase() !== "DELETE";
  return (
    <AlertDialog open={!!confirm} onOpenChange={(v)=>!v && onClose()}>
      <AlertDialogContent data-testid="storage-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-rose-600" /> {confirm.title}</AlertDialogTitle>
          <AlertDialogDescription>{confirm.body}</AlertDialogDescription>
        </AlertDialogHeader>
        {confirm.requireType ? (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="del-confirm" className="text-xs">Type <span className="font-mono font-semibold">DELETE</span> to enable the button:</Label>
            <Input id="del-confirm" value={typed} onChange={(e)=>setTyped(e.target.value)} className="font-mono" autoFocus data-testid="storage-confirm-type-input" />
          </div>
        ) : null}
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="storage-confirm-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={blocked || busy}
            onClick={async () => { setBusy(true); try { await confirm.doFn(); onClose(); } catch (e) { toast.error("Failed: " + (e?.response?.data?.detail || e.message)); } finally { setBusy(false); } }}
            className={confirm.label === "Strip photos" ? "bg-amber-700 hover:bg-amber-800" : "bg-rose-700 hover:bg-rose-800"}
            data-testid="storage-confirm-action"
          >{busy ? "Working…" : (confirm.label || "Delete")}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
